import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';
import {
  notifyQuoteCatalogMutation,
  quoteCatalogSessionResource,
} from '../quote-integration/catalogSessionResource';
import type { PackageGroupRecord } from './packageGroups';

export type PackageGroupPage = {
  items: PackageGroupRecord[];
  page: number;
  pageSize: number;
  total: number;
};

export type PackageGroupCreateRequest = {
  initialRevision: {
    name: string;
    sortOrder: number;
    mergePrices: boolean;
    participatesInDiscount: boolean;
  };
  status?: 'active' | 'inactive';
};

export type PackageGroupRevisionRequest = {
  name?: string;
  sortOrder?: number;
  mergePrices?: boolean;
  participatesInDiscount?: boolean;
  targetStatus?: 'active' | 'inactive';
  pricingImpactHash?: string;
};

export type PackageGroupMember = {
  packageId: string;
  resourceVersion: string;
};

export type PackageGroupMembersResponse = {
  items: PackageGroupMember[];
  page: number;
  pageSize: number;
  total: number;
  groupResourceVersion: string;
};

export type PackageGroupPricingImpact = {
  pricingImpactHash?: string;
  expectedPlanVersions: Array<{
    planId: string;
    resourceVersion: string;
    currentRevisionId: string;
  }>;
  affectedPlans: Array<{
    planId: string;
    resourceVersion: string;
    currentRevisionId: string;
    oldTotalCents: number | null;
    newTotalCents: number | null;
    deltaCents: number | null;
    blockedReason?: string;
  }>;
  affectedPackages: Array<{ packageId: string; resourceVersion: string }>;
};

export type PackageGroupPricingImpactOperation =
  | { kind: 'revision'; revision: PackageGroupRevisionRequest }
  | { kind: 'members'; items: PackageGroupMember[] }
  | { kind: 'delete' };

export class PackageGroupApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
  }
}

async function readProblem(response: Response): Promise<PackageGroupApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    detail?: string;
    title?: string;
  } = {};
  try {
    value = (await response.json()) as typeof value;
  } catch {
    // Keep the HTTP status when the service returns no problem body.
  }
  return new PackageGroupApiError(
    response.status,
    value.code ?? value.errorCode,
    value.detail ??
      value.title ??
      `套餐组合请求失败（HTTP ${response.status}）。`,
  );
}

function ifMatch(resourceVersion: string) {
  if (!/^[1-9]\d*$/.test(resourceVersion))
    throw new Error('套餐组合版本无效，请刷新后重试');
  return `"${resourceVersion}"`;
}

export function createPackageGroupApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  const mutationHeaders: Record<string, string> = csrfToken
    ? { 'X-CSRF-Token': csrfToken }
    : {};
  const mutation = async <T>(
    path: string,
    method: 'POST' | 'PUT',
    body: unknown,
    options: { resourceVersion?: string; idempotencyKey?: string } = {},
  ): Promise<T> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Idempotency-Key': options.idempotencyKey ?? randomUuid(),
      ...mutationHeaders,
    };
    if (options.resourceVersion)
      headers['If-Match'] = ifMatch(options.resourceVersion);
    const response = await transport.requestResponse(path, {
      method,
      headers,
      body: JSON.stringify(body),
      cache: 'no-store',
    });
    if (!response.ok) throw await readProblem(response);
    const value = (await response.json()) as T;
    notifyQuoteCatalogMutation(
      quoteCatalogSessionResource.currentScope(),
      'packages',
      [],
    );
    return value;
  };

  return {
    async list(
      query: { page?: number; pageSize?: number } = {},
      signal?: AbortSignal,
    ): Promise<PackageGroupPage> {
      const params = new URLSearchParams();
      if (query.page !== undefined) params.set('page', String(query.page));
      if (query.pageSize !== undefined)
        params.set('pageSize', String(query.pageSize));
      const suffix = params.size ? `?${params.toString()}` : '';
      const response = await transport.requestResponse(
        `/package-groups${suffix}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await readProblem(response);
      return (await response.json()) as PackageGroupPage;
    },
    async listAll(signal?: AbortSignal): Promise<PackageGroupRecord[]> {
      const first = await this.list({ page: 1, pageSize: 100 }, signal);
      if (first.items.length !== Math.min(first.total, 100))
        throw new Error('套餐组合分页结果不完整，请刷新后重试');
      const pageCount = Math.ceil(first.total / 100);
      const remaining = await Promise.all(
        Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
          this.list({ page: index + 2, pageSize: 100 }, signal),
        ),
      );
      const items = [
        ...first.items,
        ...remaining.flatMap((page) => page.items),
      ];
      if (
        items.length !== first.total ||
        new Set(items.map((item) => item.id)).size !== items.length
      )
        throw new Error('套餐组合目录存在缺失或重复项，请刷新后重试');
      return items;
    },
    async get(
      groupId: string,
      signal?: AbortSignal,
    ): Promise<PackageGroupRecord> {
      const response = await transport.requestResponse(
        `/package-groups/${encodeURIComponent(groupId)}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await readProblem(response);
      return (await response.json()) as PackageGroupRecord;
    },
    async members(
      groupId: string,
      query: { page?: number; pageSize?: number } = {},
      signal?: AbortSignal,
    ): Promise<PackageGroupMembersResponse> {
      const params = new URLSearchParams();
      if (query.page !== undefined) params.set('page', String(query.page));
      if (query.pageSize !== undefined)
        params.set('pageSize', String(query.pageSize));
      const suffix = params.size ? `?${params.toString()}` : '';
      const response = await transport.requestResponse(
        `/package-groups/${encodeURIComponent(groupId)}/members${suffix}`,
        { signal, cache: 'no-store' },
      );
      if (!response.ok) throw await readProblem(response);
      return (await response.json()) as PackageGroupMembersResponse;
    },
    async membersAll(
      groupId: string,
      signal?: AbortSignal,
    ): Promise<PackageGroupMembersResponse> {
      const pageSize = 200;
      const first = await this.members(groupId, { page: 1, pageSize }, signal);
      if (first.items.length !== Math.min(first.total, pageSize))
        throw new Error('套餐组合成员分页不完整，请刷新后重试');
      const pageCount = Math.ceil(first.total / pageSize);
      const remaining = await Promise.all(
        Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
          this.members(groupId, { page: index + 2, pageSize }, signal),
        ),
      );
      const pages = [first, ...remaining];
      if (
        pages.some(
          (page) => page.groupResourceVersion !== first.groupResourceVersion,
        )
      )
        throw new Error('套餐组合成员读取期间版本已变化，请重新读取');
      const items = pages.flatMap((page) => page.items);
      if (
        items.length !== first.total ||
        new Set(items.map((item) => item.packageId)).size !== items.length
      )
        throw new Error('套餐组合成员目录不完整或存在重复项，请刷新后重试');
      return { ...first, items, page: 1, pageSize, total: first.total };
    },
    create: (body: PackageGroupCreateRequest, idempotencyKey?: string) =>
      mutation<PackageGroupRecord>('/package-groups', 'POST', body, {
        idempotencyKey,
      }),
    createRevision: (
      groupId: string,
      resourceVersion: string,
      body: PackageGroupRevisionRequest,
      idempotencyKey?: string,
    ) =>
      mutation<PackageGroupRecord>(
        `/package-groups/${encodeURIComponent(groupId)}/revisions`,
        'POST',
        body,
        { resourceVersion, idempotencyKey },
      ),
    previewPricingImpact: (
      groupId: string,
      resourceVersion: string,
      operation: PackageGroupPricingImpactOperation,
      signal?: AbortSignal,
    ): Promise<PackageGroupPricingImpact> =>
      transport
        .requestResponse(
          `/package-groups/${encodeURIComponent(groupId)}/price-impact-preview`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'If-Match': ifMatch(resourceVersion),
              ...mutationHeaders,
            },
            body: JSON.stringify({ operation }),
            signal,
            cache: 'no-store',
          },
        )
        .then(async (response) => {
          if (!response.ok) throw await readProblem(response);
          return (await response.json()) as PackageGroupPricingImpact;
        }),
    replaceMembers: (
      groupId: string,
      groupResourceVersion: string,
      body: { items: PackageGroupMember[]; pricingImpactHash?: string },
      idempotencyKey?: string,
    ) =>
      mutation<PackageGroupMembersResponse>(
        `/package-groups/${encodeURIComponent(groupId)}/members`,
        'PUT',
        body,
        { resourceVersion: groupResourceVersion, idempotencyKey },
      ),
    async deleteGroup(
      groupId: string,
      resourceVersion: string,
      pricingImpactHash: string,
      idempotencyKey = randomUuid(),
    ): Promise<void> {
      if (!/^[a-f0-9]{64}$/.test(pricingImpactHash))
        throw new Error('套餐组合删除预览无效，请重新预览');
      const response = await transport.requestResponse(
        `/package-groups/${encodeURIComponent(groupId)}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'If-Match': ifMatch(resourceVersion),
            'Idempotency-Key': idempotencyKey,
            ...mutationHeaders,
          },
          body: JSON.stringify({ pricingImpactHash }),
          cache: 'no-store',
        },
      );
      if (!response.ok) throw await readProblem(response);
      notifyQuoteCatalogMutation(
        quoteCatalogSessionResource.currentScope(),
        'packages',
        [],
      );
    },
  };
}
